package org.teddapp.project.ui.portfolio

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tedd.db.Competence
import com.tedd.db.Competence_criterion
import com.tedd.db.Competence_family
import org.teddapp.project.portfolio.CompetenceLevel
import org.teddapp.project.portfolio.export.PortfolioDocumentBuilder
import org.teddapp.project.ui.ChipSize
import org.teddapp.project.ui.ChipTone
import org.teddapp.project.ui.ReorderableColumn
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddChip
import org.teddapp.project.ui.TeddField
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddTierBadge
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.rememberReorderableState
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import org.teddapp.project.ui.TeddFilterRow
import org.teddapp.project.ui.TeddEmptyState
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.teddClickable
import org.teddapp.project.ui.teddCardShadow

// ── Familles ───────────────────────────────────────────────────────────────

/** Colonne des familles (grand écran) : « Toutes », puis chaque famille et son nombre de compétences. */
@Composable
internal fun FamiliesColumn(model: PortfolioModel) {
    PfEyebrow("Familles", modifier = Modifier.padding(start = 10.dp, bottom = 6.dp))
    TeddFilterRow(
        label = "Toutes",
        selected = model.selectedFamilyId == null,
        onClick = { model.selectedFamilyId = null },
        count = model.competences.size,
        leading = { TeddIcon(name = "list", size = 16.dp, color = TeddTokens.InkMuted) },
    )
    model.families.forEach { family ->
        TeddFilterRow(
            label = family.name,
            selected = model.selectedFamilyId == family.id,
            onClick = { model.selectedFamilyId = family.id },
            count = model.competences.count { it.family_id == family.id },
            leading = { FamilyDot(family.color, family.icon, size = 18.dp) },
        )
    }
    TeddButton(
        text = "Nouvelle famille",
        onClick = { model.editor = PortfolioEditor.Family(null) },
        kind = TeddButtonKind.Ghost,
        size = TeddButtonSize.Sm,
        modifier = Modifier.padding(top = 6.dp),
        icon = "plus",
    )
}

/** Familles en chips défilantes (téléphone). */
@Composable
internal fun FamilyChipsRow(model: PortfolioModel) {
    Row(
        modifier = Modifier.fillMaxWidth().horizontalScroll(rememberScrollState()),
        horizontalArrangement = Arrangement.spacedBy(6.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        FamilyChip("Toutes", null, model.selectedFamilyId == null) { model.selectedFamilyId = null }
        model.families.forEach { f ->
            FamilyChip(f.name, parseHex(f.color, TeddTokens.LimeStrong), model.selectedFamilyId == f.id) { model.selectedFamilyId = f.id }
        }
        org.teddapp.project.ui.TeddFilterChip(label = "Famille", selected = false, onClick = { model.editor = PortfolioEditor.Family(null) }, icon = "plus")
    }
}

@Composable
private fun FamilyChip(label: String, color: Color?, on: Boolean, onClick: () -> Unit) {
    org.teddapp.project.ui.TeddFilterChip(label = label, selected = on, onClick = onClick, dotColor = color)
}

// ── Compétences ────────────────────────────────────────────────────────────

/** En-tête de la liste : la famille courante, ses boutons, la recherche. */
@Composable
internal fun CompetencesHeader(model: PortfolioModel, compact: Boolean) {
    val family = model.selectedFamily
    val list = model.competencesOf(model.selectedFamilyId)
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3), modifier = Modifier.fillMaxWidth()) {
        Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
            Column(modifier = Modifier.weight(1f).padding(start = if (compact) 0.dp else TeddSpace.S2)) {
                Text(
                    text = family?.name ?: "Toutes les compétences",
                    style = TeddType.BodyStrong,
                    color = TeddTokens.Ink,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    text = "${list.size} compétence${if (list.size > 1) "s" else ""}",
                    style = TeddType.MonoSm.copy(fontSize = 11.sp),
                    color = TeddTokens.InkMuted,
                )
            }
            if (family != null) {
                TeddIconButton(onClick = { model.editor = PortfolioEditor.Family(family) }, size = 28.dp) {
                    TeddIcon(name = "edit", size = 14.dp, color = TeddTokens.InkMuted)
                }
                TeddIconButton(onClick = { model.confirmDelete = DeleteTarget.Family(family) }, size = 28.dp) {
                    TeddIcon(name = "trash", size = 14.dp, color = TeddTokens.InkMuted)
                }
            }
            if (model.families.isNotEmpty()) {
                TeddButton(
                    text = if (compact) "" else "Nouvelle",
                    onClick = { model.editor = PortfolioEditor.Competence(null, model.selectedFamilyId ?: model.families.firstOrNull()?.id) },
                    kind = TeddButtonKind.Secondary,
                    size = TeddButtonSize.Sm,
                    icon = "plus",
                )
            }
        }
        family?.description?.takeIf { it.isNotBlank() }?.let {
            Text(it, color = TeddTokens.InkMuted, style = TeddType.Caption, modifier = Modifier.padding(horizontal = if (compact) 0.dp else TeddSpace.S2))
        }
        if (model.competences.size > 4) {
            TeddField(label = "Recherche", value = model.search, onValueChange = { model.search = it }, placeholder = "Nom, description…")
        }
    }
}

/** Ligne d'une compétence dans la liste : celle qu'on lit se pose sur la surface. */
@Composable
internal fun CompetenceRow(c: Competence, model: PortfolioModel, selected: Boolean, showFamily: Boolean, card: Boolean = false, onClick: () -> Unit) {
    val level = model.levelOf(c.id)
    val crits = model.criteriaOf(c.id)
    val family = model.familiesById[c.family_id]
    val skill = model.skillLinkOf(c)
    val shape = RoundedCornerShape(TeddTokens.RMd)
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .then(if (selected) Modifier.teddCardShadow(shape) else Modifier)
            .then(if (card) Modifier.border(1.dp, TeddTokens.Line, shape) else Modifier)
            .teddClickable(
                onClick = onClick,
                shape = shape,
                fill = if (selected || card) TeddTokens.Surface else Color.Transparent,
                hover = if (selected || card) TeddTokens.Surface else TeddTokens.SurfaceSunken,
                role = null,
            )
            .padding(horizontal = TeddSpace.S3, vertical = 10.dp),
        verticalArrangement = Arrangement.spacedBy(TeddSpace.S2),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            Column(modifier = Modifier.weight(1f)) {
                Text(c.name, color = TeddTokens.Ink, style = TeddType.BodyStrong, maxLines = 1, overflow = TextOverflow.Ellipsis)
                if (showFamily && family != null) Text(family.name, color = TeddTokens.InkMuted, style = TeddType.Caption, maxLines = 1)
            }
            LevelBadge(level, small = true)
        }
        LevelDistributionBar(model.distributionOf(c.id), height = 4.dp)
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            Text(
                text = if (crits.isEmpty()) "Aucun critère" else "${crits.size} critère${if (crits.size > 1) "s" else ""}",
                color = TeddTokens.InkMuted,
                style = TeddType.MonoSm.copy(fontSize = 11.sp),
                modifier = Modifier.weight(1f),
            )
            if (skill != null) {
                Text(PortfolioDocumentBuilder.fmtHours(skill.hours), color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp))
                skill.tier?.let { TeddTierBadge(it) }
            }
        }
    }
}

/** Détail d'une compétence : sa fiche, sa skill liée, ses critères, les pièces qui la démontrent. */
@Composable
internal fun CompetenceDetail(c: Competence, model: PortfolioModel, compact: Boolean, onBack: (() -> Unit)? = null) {
    val family = model.familiesById[c.family_id]
    val crits = model.criteriaOf(c.id)
    val level = model.levelOf(c.id)
    val skill = model.skillLinkOf(c)
    val pieces = model.piecesDemonstrating(c.id)
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S6), modifier = Modifier.fillMaxWidth()) {
        if (onBack != null) {
            TeddButton(text = "Compétences", onClick = onBack, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, icon = "chevron-left")
        }
        // En-tête
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                if (family != null) {
                    FamilyDot(family.color, family.icon, size = 20.dp)
                    Text(family.name, style = TeddType.Caption, color = TeddTokens.InkMuted)
                }
                Spacer(modifier = Modifier.weight(1f))
                TeddButton(text = "Modifier", onClick = { model.editor = PortfolioEditor.Competence(c, c.family_id) }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, icon = "edit")
                TeddIconButton(onClick = { model.confirmDelete = DeleteTarget.Competence(c) }) {
                    TeddIcon(name = "trash", size = 16.dp, color = TeddTokens.InkMuted)
                }
            }
            org.teddapp.project.ui.TeddFitText(c.name, style = if (compact) TeddType.Title2 else TeddType.Title1, minFontSize = 16.sp, maxLines = 3)
            c.description?.takeIf { it.isNotBlank() }?.let { Text(it, style = TeddType.BodyLg, color = TeddTokens.InkMuted) }
        }
        // Niveau agrégé
        TeddCard(padding = PaddingValues(TeddSpace.S5)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
                Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
                    PfEyebrow("Niveau")
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                        if (level != null) Box(modifier = Modifier.size(10.dp).background(levelColor(level), CircleShape))
                        Text(
                            text = level?.label ?: "À évaluer",
                            color = if (level != null) TeddTokens.Ink else TeddTokens.InkMuted,
                            style = TeddType.Title2,
                        )
                    }
                }
                Text(
                    text = if (crits.isEmpty()) "aucun critère" else "${crits.size} critère${if (crits.size > 1) "s" else ""}",
                    color = TeddTokens.InkMuted,
                    style = TeddType.MonoSm,
                )
            }
            Spacer(modifier = Modifier.height(TeddSpace.S4))
            LevelDistributionBar(model.distributionOf(c.id), height = 8.dp)
            if (crits.isNotEmpty()) {
                Spacer(modifier = Modifier.height(TeddSpace.S3))
                val d = model.distributionOf(c.id)
                Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S4)) {
                    for (i in 3 downTo 0) if (d[i] > 0) {
                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                            Box(modifier = Modifier.size(8.dp).background(levelColor(CompetenceLevel.of(i)), CircleShape))
                            Text("${d[i]} ${CompetenceLevel.of(i).label.lowercase()}", color = TeddTokens.InkMuted, style = TeddType.Caption)
                        }
                    }
                }
            }
        }
        // Skill Focus liée
        if (c.skill_id != null) {
            TeddCard(padding = PaddingValues(TeddSpace.S4), tonal = true) {
                PfEyebrow("Compétence suivie dans Focus")
                Spacer(modifier = Modifier.height(6.dp))
                if (skill?.skill == null) {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                        Text("La compétence suivie a été supprimée.", color = TeddTokens.InkMuted, style = TeddType.Body, modifier = Modifier.weight(1f))
                        TeddButton(text = "Délier", onClick = { model.unlinkSkill(c) }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm)
                    }
                } else {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                        Text(skill.skill.name, color = TeddTokens.Ink, style = TeddType.BodyStrong, modifier = Modifier.weight(1f))
                        Text(PortfolioDocumentBuilder.fmtHours(skill.hours) + " de focus", color = TeddTokens.InkMuted, style = TeddType.MonoSm)
                        skill.tier?.let { TeddTierBadge(it) }
                    }
                }
            }
        }
        // Critères
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                PfEyebrow("Critères · ${crits.size}", modifier = Modifier.weight(1f))
                TeddButton(
                    text = "Critère",
                    onClick = { model.editor = PortfolioEditor.Criterion(null, c.id) },
                    kind = TeddButtonKind.Secondary,
                    size = TeddButtonSize.Sm,
                    icon = "plus",
                )
            }
            if (crits.isEmpty()) {
                Text(
                    "Découpe la compétence en critères observables, puis évalue chacun d'un clic.",
                    color = TeddTokens.InkMuted, style = TeddType.Body,
                )
            } else {
                val state = rememberReorderableState()
                ReorderableColumn(
                    items = crits,
                    keyOf = { it.id },
                    state = state,
                    onReorder = { from, to -> model.reorderCriteria(c.id, from, to) },
                    modifier = Modifier.fillMaxWidth(),
                ) { _, crit, handle ->
                    CriterionRow(crit, model, compact, handle)
                }
            }
        }
        // Pièces qui démontrent
        if (pieces.isNotEmpty()) {
            Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
                PfEyebrow("Démontrée par · ${pieces.size}", modifier = Modifier.padding(bottom = TeddSpace.S1))
                pieces.forEach { p ->
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .teddClickable(onClick = { model.openPiece(p.id) }, role = null)
                            .padding(horizontal = TeddSpace.S2, vertical = TeddSpace.S2),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
                    ) {
                        TeddIcon(name = "cards", size = 16.dp, color = TeddTokens.InkMuted)
                        Text(p.title, color = TeddTokens.Ink, style = TeddType.Body, modifier = Modifier.weight(1f), maxLines = 1, overflow = TextOverflow.Ellipsis)
                        TeddIcon(name = "chevron-right", size = 14.dp, color = TeddTokens.InkSubtle)
                    }
                }
            }
        }
    }
}

/** Un critère : libellé, description, niveau à choisir, date d'évaluation. */
@Composable
private fun CriterionRow(crit: Competence_criterion, model: PortfolioModel, compact: Boolean, handle: Modifier) {
    val level = CompetenceLevel.of(crit.level)
    val shape = RoundedCornerShape(TeddTokens.RMd)
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(bottom = TeddSpace.S2)
            .background(TeddTokens.Surface, shape)
            .border(1.dp, TeddTokens.Line, shape)
            .padding(horizontal = TeddSpace.S3, vertical = TeddSpace.S3),
        verticalArrangement = Arrangement.spacedBy(TeddSpace.S3),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            Box(modifier = handle) { TeddIcon(name = "grip", size = 14.dp, color = TeddTokens.InkSubtle) }
            Column(modifier = Modifier.weight(1f)) {
                Text(crit.label, color = TeddTokens.Ink, style = TeddType.BodyStrong)
                crit.description?.takeIf { it.isNotBlank() }?.let { Text(it, color = TeddTokens.InkMuted, style = TeddType.Caption) }
            }
            TeddIconButton(onClick = { model.editor = PortfolioEditor.Criterion(crit, crit.competence_id) }, size = 28.dp) {
                TeddIcon(name = "edit", size = 14.dp, color = TeddTokens.InkMuted)
            }
            TeddIconButton(onClick = { model.confirmDelete = DeleteTarget.Criterion(crit) }, size = 28.dp) {
                TeddIcon(name = "trash", size = 14.dp, color = TeddTokens.InkMuted)
            }
        }
        LevelPicker(level, onChange = { model.setLevel(crit, it) }, dense = compact, modifier = Modifier.fillMaxWidth())
        val evaluated = crit.evaluated_at?.let { PortfolioDocumentBuilder.isoFromMillis(it) }
            ?.let { "évalué le ${org.teddapp.project.ui.TeddDates.formatShort(it, withYear = true) ?: it}" }
        val note = crit.evaluation_note?.takeIf { it.isNotBlank() }
        if (evaluated != null || note != null) {
            Text(
                text = listOfNotNull(evaluated, note).joinToString(" · "),
                color = TeddTokens.InkMuted,
                style = TeddType.Caption,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}

/** Liste des compétences de la famille courante, ou de toutes. */
@Composable
internal fun CompetencesList(model: PortfolioModel, compact: Boolean, onOpen: (Competence) -> Unit) {
    val list = model.competencesOf(model.selectedFamilyId)
    if (model.families.isEmpty()) {
        RecueilEmpty(model)
        return
    }
    if (list.isEmpty()) {
        Text(
            if (model.search.isNotBlank()) "Aucune compétence ne correspond." else "Aucune compétence ici. Ajoute la première.",
            color = TeddTokens.InkMuted, style = TeddType.Caption, modifier = Modifier.padding(TeddSpace.S2),
        )
        return
    }
    list.forEach { c ->
        CompetenceRow(c, model, selected = !compact && c.id == model.selectedCompetenceId, showFamily = model.selectedFamilyId == null, card = compact) { onOpen(c) }
    }
}

@Composable
internal fun RecueilEmpty(model: PortfolioModel) {
    TeddEmptyState(
        title = "Un recueil vide",
        message = "Commence par une famille (« Données », « Communication »…), puis décris chaque compétence en critères observables que tu évalueras sur quatre niveaux.",
        icon = "portfolio",
        actionLabel = "Créer une famille",
        onAction = { model.editor = PortfolioEditor.Family(null) },
    )
}

@Suppress("unused")
private fun Competence_family.dot() = color
